import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import Pouch from "@/components/Pouch";

const WEIGHTS = [
  { g: 250, mult: 1 },
  { g: 500, mult: 1.9 },
  { g: 1000, mult: 3.6 },
];
const GRINDS = ["whole-bean", "coarse", "medium", "fine"];

export default function ProductDetail() {
  const { id } = useParams();
  const [blend, setBlend] = useState(null);
  const [weight, setWeight] = useState(250);
  const [grind, setGrind] = useState("whole-bean");
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    axios.get(`${API}/blends/${id}`).then((r) => setBlend(r.data));
  }, [id]);

  if (!blend) return <div className="pt-40 text-center label-mono">Loading blend…</div>;
  const mult = WEIGHTS.find((w) => w.g === weight).mult;
  const price = Math.round(blend.price_inr * mult);

  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-32 pb-24">
      <Link to="/shop" className="label-mono link-underline">← Back to shop</Link>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mt-8">
        <div className="md:col-span-7">
          <div className="aspect-square overflow-hidden" style={{ background: "var(--surface-warm)" }}>
            <Pouch blend={blend} weightLabel={weight >= 1000 ? "1 KG" : `${weight} G`} />
          </div>
        </div>
        <div className="md:col-span-5">
          <div className="label-mono mb-3 text-[var(--text-muted)]">{blend.origin}</div>
          {blend.most_popular && (
            <div className="mb-4 inline-block px-4 py-1.5" data-testid="pdp-popular-badge" style={{
              background: "linear-gradient(180deg, #FF5C5F 0%, #E5383F 60%, #A3152A 100%)",
              color: "#FFFFFF",
              fontFamily: "'Fraunces', serif",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.24em",
              border: "1px solid #7A0F1F",
              boxShadow: "0 1px 0 rgba(255,240,210,0.4) inset, 0 4px 12px rgba(0,0,0,0.25)",
              textShadow: "0 -1px 0 rgba(0,0,0,0.3)",
            }}>
              ★ MOST POPULAR · HIGH SELLING ★
            </div>
          )}
          <h1 className="font-serif text-5xl md:text-6xl leading-none tracking-tighter">{blend.name}</h1>
          <div className="flex gap-2 mt-6 flex-wrap">
            {blend.flavor_notes.map((n) => (
              <span key={n} className="badge">{n}</span>
            ))}
          </div>
          <p className="mt-8 text-[var(--text-secondary)] leading-relaxed">{blend.description}</p>

          <div className="mt-10 pt-8 border-t border-[var(--border)] space-y-8">
            <Spec label="Roast" value={blend.roast_level} />
            {(blend.arabica_pct + blend.robusta_pct + blend.chicory_pct) > 0 && (
              <Spec label="Composition" value={`${blend.arabica_pct}% Arabica · ${blend.robusta_pct}% Robusta${blend.chicory_pct ? ` · ${blend.chicory_pct}% Chicory` : ""}`} />
            )}
          </div>

          <div className="mt-10 space-y-6">
            <div>
              <div className="label-mono mb-3">Weight</div>
              <div className="flex gap-2 flex-wrap">
                {WEIGHTS.map((w) => (
                  <button
                    key={w.g}
                    onClick={() => setWeight(w.g)}
                    className={`px-4 py-3 border transition-colors label-mono ${
                      weight === w.g ? "border-[var(--primary)] text-[var(--primary)]" : "border-[var(--border)] text-[var(--text-secondary)]"
                    }`}
                    data-testid={`weight-${w.g}`}
                  >
                    {w.g >= 1000 ? "1 kg" : `${w.g} g`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="label-mono mb-3">Grind</div>
              <div className="flex gap-2 flex-wrap">
                {GRINDS.map((g) => (
                  <button
                    key={g}
                    onClick={() => setGrind(g)}
                    className={`px-4 py-3 border transition-colors label-mono ${
                      grind === g ? "border-[var(--primary)] text-[var(--primary)]" : "border-[var(--border)] text-[var(--text-secondary)]"
                    }`}
                    data-testid={`grind-${g}`}
                  >
                    {g.replace("-", " ")}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-end gap-4 pt-6 border-t border-[var(--border)]">
              <div>
                <div className="label-mono">Quantity</div>
                <div className="flex items-center border border-[var(--border)] mt-2">
                  <button className="px-4 py-3" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="pd-qty-dec">−</button>
                  <span className="font-mono px-4">{qty}</span>
                  <button className="px-4 py-3" onClick={() => setQty(qty + 1)} data-testid="pd-qty-inc">+</button>
                </div>
              </div>
              <div className="ml-auto text-right">
                <div className="label-mono">Total</div>
                <div className="font-mono text-3xl text-[var(--primary)]">₹{price * qty}</div>
              </div>
            </div>

            <button
              className="btn-highlight w-full"
              data-testid="add-to-cart-button"
              onClick={() =>
                addItem({
                  blend_id: blend.id,
                  name: `${blend.name} · ${weight >= 1000 ? "1kg" : weight + "g"}`,
                  price_inr: price,
                  quantity: qty,
                  weight_g: weight,
                  grind,
                  image_url: blend.image_url,
                  is_custom: false,
                  blend_snapshot: {
                    name: blend.name,
                    flavor_notes: blend.flavor_notes,
                    arabica_pct: blend.arabica_pct,
                    robusta_pct: blend.robusta_pct,
                    chicory_pct: blend.chicory_pct,
                  },
                })
              }
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="label-mono">{label}</span>
      <span className="font-mono text-sm text-[var(--text)] capitalize">{value}</span>
    </div>
  );
}
